import React, { useEffect, useState } from 'react';
import { Drawer, Spin, Alert, Typography, Space, Tag, Divider, Empty, Descriptions } from 'antd';
import { AlertOutlined, UserOutlined } from '@ant-design/icons';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import { getVoucherSummaryApi } from 'api/hallazgoFlujo';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import { useTenantColors } from 'hooks/useTenantColors';
import { drawerPropsFromLevel, fmtDateTime } from './drawerShared';

// Fase 9: drawer de summary de voucher de emergencia.

function VoucherSummaryDrawer({ open, idVoucher, onClose, level = 0 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { openHallazgo } = useTrazabilidadDrawer();
  const { secondary } = useTenantColors();

  useEffect(() => {
    if (!open || !idVoucher) return;
    setLoading(true);
    setError(null);
    setData(null);
    getVoucherSummaryApi(idVoucher)
      .then((res) => {
        if (res && res.id) setData(res);
        else setError(res?.message || 'No se pudo cargar el voucher');
      })
      .catch((err) => setError(err?.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [open, idVoucher]);

  return (
    <Drawer
      {...drawerPropsFromLevel(level)}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnHidden
      title={
        <Space align="center" size="middle">
          <AlertOutlined style={{ color: secondary }} />
          <span>Voucher {idVoucher ? `#${idVoucher}` : ''}</span>
          {data?.reason?.es_emergencia ? (
            <Tag color="red">EMERGENCIA</Tag>
          ) : data?.reason ? (
            <Tag>{data.reason.nombre}</Tag>
          ) : null}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : data ? (
        <>
          {data.cliente ? (
            <div style={{ marginBottom: 12 }}>
              <Space align="center">
                <UserOutlined style={{ color: '#64748b' }} />
                <Typography.Text strong>{data.cliente.name}</Typography.Text>
              </Space>
            </div>
          ) : null}

          <Descriptions
            column={1}
            size="small"
            bordered
            labelStyle={{ width: 140, background: '#f6f7f9' }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Fecha">{fmtDateTime(data.date)}</Descriptions.Item>
            {data.specialist_rut ? (
              <Descriptions.Item label="Técnico">{data.specialist_rut}</Descriptions.Item>
            ) : null}
            {data.equipment ? (
              <Descriptions.Item label="Equipo">{data.equipment}</Descriptions.Item>
            ) : null}
            {data.attention_init || data.attention_finish ? (
              <Descriptions.Item label="Atención">
                {data.attention_init ?? '—'} — {data.attention_finish ?? '—'}
              </Descriptions.Item>
            ) : null}
            {data.finish_state ? (
              <Descriptions.Item label="Estado final">{data.finish_state}</Descriptions.Item>
            ) : null}
            {data.recepcionist_name ? (
              <Descriptions.Item label="Recibió">
                {data.recepcionist_name}
                {data.recepcionist_charge ? ` (${data.recepcionist_charge})` : ''}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {data.observations ? (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text
                strong
                style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}
              >
                OBSERVACIONES
              </Typography.Text>
              <Typography.Paragraph style={{ marginTop: 4, fontSize: 13 }}>
                {data.observations}
              </Typography.Paragraph>
            </div>
          ) : null}

          <Divider style={{ margin: '12px 0' }} />

          <Typography.Text
            strong
            style={{
              fontSize: 11,
              letterSpacing: 0.8,
              color: '#64748b',
              display: 'block',
              marginBottom: 8,
            }}
          >
            HALLAZGOS DETECTADOS · {data.hallazgos?.length ?? 0}
          </Typography.Text>
          {data.hallazgos && data.hallazgos.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.hallazgos.map((h) => (
                <HallazgoChip key={h.id} hallazgo={h} onClick={() => openHallazgo(h.id)} />
              ))}
            </div>
          ) : (
            <Empty
              description="Sin hallazgos detectados en esta atención"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </>
      ) : null}
    </Drawer>
  );
}

export default VoucherSummaryDrawer;
